import { eventStatusToNumber, IN_PLAY_STATUS_IDS } from "../../Trader/Cricket/decimalEventStatus";
import { isCricketHundredMatch, isCricketTestMatch } from "../../../utils/cricketTestMatch";

/**
 * Build ctx for {@link isCricketTestMatch} / {@link isCricketHundredMatch} from lobby fixture + stream snapshot.
 * Keep aligned with Squares match-play unsupported-format checks.
 * @param {object | undefined | null} fixture
 * @param {object | undefined | null} streamData
 */
export function buildSquaresFormatCheckContext(fixture, streamData) {
  const mu = streamData?.market_update;
  return {
    format:
      fixture?.format ??
      fixture?.Format ??
      streamData?.event?.format ??
      streamData?.match_update?.format,
    mOvers: streamData?.mOvers ?? streamData?.event?.mOvers,
    maxOvers: streamData?.event?.max_overs,
    competition: fixture?.competition ?? streamData?.event?.competition,
    matchName: fixture?.name ?? fixture?.match_name,
    streamData,
    markets: mu?.markets ?? mu?.market_update?.markets,
  };
}

/**
 * False for Test cricket and The Hundred — same product rules as Squares match-play.
 * @param {object | undefined | null} fixture
 * @param {object | undefined | null} streamData
 */
export function isSquaresSupportedCricketFormat(fixture, streamData) {
  const ctx = buildSquaresFormatCheckContext(fixture, streamData);
  if (isCricketTestMatch(ctx)) return false;
  if (isCricketHundredMatch(ctx)) return false;
  return true;
}

/**
 * Match Centre / Matches list PLAY chips: show Squares when the fixture is in the
 * offer-ready lobby set (not scheduled / pretoss) and the format is supported.
 *
 * Do not require stream 410 markets. Listings subscribe with `product=trader`, and
 * trader lobby SSE drops 410s, so PLAY OVER N never resolves on these cards even
 * when Squares is available.
 *
 * @param {object | undefined | null} fixture
 * @param {object | undefined | null} streamData
 * @param {boolean} offerReady — from useSquaresOfferMatchIds
 * @returns {boolean}
 */
export function isSquaresOfferedOnMatchListCard(fixture, streamData, offerReady) {
  if (!offerReady) return false;
  return isSquaresSupportedCricketFormat(fixture, streamData);
}

/**
 * Match completion / lobby visibility — keep in sync with Squares lobby (UpcomingMatches).
 * @param { { status?: string | null } } fixture
 */
export function isSquaresLobbyMatchCompleted(fixture) {
  if (!fixture || typeof fixture !== "object") return false;
  const ls = fixture.liveScore;
  if (ls && typeof ls === "object" && ls.isFinished === true) return true;
  if (!fixture.status) return false;
  const full = String(fixture.status);
  const status = full.split(" - ");
  let matchstatus = status[0].replace(/\s*\(.*?\)\s*/g, "").trim();
  if (full.includes("Match in Progress")) {
    matchstatus = "IN-PLAY";
  }
  const upperStatus = matchstatus.toUpperCase();
  const uFull = full.toUpperCase();
  return (
    upperStatus.startsWith("CANCELLED") ||
    upperStatus.startsWith("MATCH ABANDONED") ||
    upperStatus.startsWith("MATCH COMPLETE") ||
    upperStatus.startsWith("EVENT CLOSED") ||
    upperStatus.startsWith("FINISHED") ||
    upperStatus.startsWith("FULL TIME") ||
    uFull.includes("MATCH COMPLETE") ||
    uFull.includes("EVENT CLOSED") ||
    uFull.includes("FINISHED") ||
    uFull.includes("FULL TIME") ||
    uFull.includes("EVENT_FULL_RESULT") ||
    uFull.includes("MATCH ABANDONED")
  );
}

/**
 * Normalise prioritised API events to lobby card shape (match_id) and drop completed.
 * @param {unknown[]} events
 */
export function mapAndFilterSquaresLobbyFixtures(events) {
  const list = Array.isArray(events) ? events : [];
  const asFixtures = list.map((ev) => ({
    ...ev,
    match_id: ev.id ?? ev.match_id,
  }));
  return asFixtures.filter((fixture) => !isSquaresLobbyMatchCompleted(fixture));
}

/**
 * Normalised list status line (aligns with SquaresMatchCard matchStatus for "Match in Progress").
 * @param { { status?: string | null } } fixture
 */
export function listNormalizedMatchStatusForSquares(fixture) {
  const rawFull = fixture?.status || "";
  const fixtureStatusUpper = rawFull.toUpperCase();
  const rawStatus = rawFull.split(" - ")[0] || "";
  let normalized = rawStatus.replace(/\s*\(.*?\)\s*/g, "").trim();
  if (rawFull.includes("Match in Progress")) {
    normalized = "IN-PLAY";
  }
  if (fixtureStatusUpper.includes("BREAK IN PLAY")) {
    normalized = "BREAK IN PLAY";
  }
  return normalized;
}

/**
 * Whether Squares is offered for play on Matches/homepage cards (not merely listed in the lobby).
 * Aligns with Squares lobby CTAs that are not yet playable: "OPENS IN …" (scheduled) and
 * "AWAITING TOSS" (pretoss / pre-match). Does not require SSE.
 * @param { { status?: string | null, liveScore?: { isFinished?: boolean } } } fixture
 */
export function isSquaresFixturePlayableForHomeBadge(fixture) {
  if (!fixture || typeof fixture !== "object") return false;
  if (isSquaresLobbyMatchCompleted(fixture)) return false;
  const full = String(fixture.status || "");
  const fullUpper = full.toUpperCase();
  if (fullUpper.includes("CANCELLED") || fullUpper.includes("MATCH ABANDONED")) return false;
  const norm = listNormalizedMatchStatusForSquares(fixture);
  const normUpper = norm.toUpperCase();
  // Prematch / scheduled — Squares lobby shows OPENS IN / OPENS SOON
  if (normUpper === "MATCH SCHEDULED" || fullUpper.includes("MATCH SCHEDULED")) return false;
  // Pretoss — Squares lobby shows AWAITING TOSS until toss confirms play
  if (
    fullUpper.includes("TOSS PENDING") ||
    fullUpper.includes("TOSS DELAYED") ||
    normUpper === "TOSS PENDING" ||
    normUpper === "TOSS DELAYED"
  ) {
    return false;
  }
  return true;
}

/**
 * Same as lobby list minus scheduled / pretoss fixtures (for Matches cards + "games available" counts).
 * @param {unknown[]} events
 */
export function mapFilterSquaresPlayableLobbyFixturesForHome(events) {
  return mapAndFilterSquaresLobbyFixtures(events).filter((f) => isSquaresFixturePlayableForHomeBadge(f));
}

/**
 * @param {unknown[]} events
 */
export function countSquaresPlayableLobbyFixturesForHome(events) {
  return mapFilterSquaresPlayableLobbyFixturesForHome(events).length;
}

/**
 * True when the match is treated as live for Squares (in-play from fixture list and/or stream event_status).
 * Used to show only live rows in the friend-challenge modal.
 * Also requires offer-ready status (excludes scheduled / pretoss even if stream looks live early).
 * @param {object} fixture
 * @param {object | undefined} streamData
 */
export function isSquaresFixtureLiveForChallengeList(fixture, streamData) {
  if (!fixture) return false;
  if (!isSquaresFixturePlayableForHomeBadge(fixture)) return false;
  const norm = listNormalizedMatchStatusForSquares(fixture);
  if (norm === "IN-PLAY" || norm === "BREAK IN PLAY") return true;
  const mu = streamData?.market_update;
  const es = mu?.event_status ?? mu?.market_update?.event_status ?? null;
  const n = eventStatusToNumber(es);
  if (n != null && IN_PLAY_STATUS_IDS.includes(n)) return true;
  return false;
}
